"use strict";
/**
 * Accumulation-stage projection.
 *
 * Iterates month-by-month with END-of-month SIP contributions:
 *
 *   balanceAfterGrowth = openingBalance × (1 + monthlyRate)
 *   contribution       = startingSIP × (1 + annualStepUp) ^ floor((m-1)/12)
 *   endingBalance      = balanceAfterGrowth + contribution
 *
 * The opening balance at month 1 is
 *   existingRetirementInvestments + lumpSumAvailableNow.
 *
 * `expectedLumpSumAtRetirement` is added ONCE after the last month,
 * outside this loop.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.unitStepUpSipFutureValueFactor = exports.projectAccumulation = void 0;
const rates_1 = require("./rates");
function accumulationYearsFrom(inputs) {
    return Math.max(0, inputs.retirementAge - inputs.currentAge);
}
function projectAccumulation(inputs) {
    const years = accumulationYearsFrom(inputs);
    const totalMonths = years * 12;
    const monthlyRate = (0, rates_1.effectiveAnnualToMonthly)(inputs.accumulationReturn);
    const months = [];
    const years_ = [];
    const openingSeed = (inputs.existingRetirementInvestments || 0) +
        (inputs.lumpSumAvailableNow || 0);
    let balance = openingSeed;
    let totalContributions = 0;
    let contributionsInYear = 0;
    for (let m = 1; m <= totalMonths; m++) {
        const opening = balance;
        const balanceAfterGrowth = opening * (1 + monthlyRate);
        const stepUps = Math.floor((m - 1) / 12);
        const contribution = (inputs.currentMonthlySip || 0) *
            Math.pow(1 + (inputs.annualSipStepUp || 0), stepUps);
        const ending = balanceAfterGrowth + contribution;
        months.push({
            monthIndex: m,
            age: inputs.currentAge + m / 12,
            openingBalance: opening,
            balanceAfterGrowth,
            contribution,
            endingBalance: ending,
        });
        balance = ending;
        totalContributions += contribution;
        contributionsInYear += contribution;
        if (m % 12 === 0) {
            years_.push({
                yearIndex: m / 12,
                ageAtEnd: inputs.currentAge + m / 12,
                contributionsThisYear: contributionsInYear,
                endingBalance: ending,
            });
            contributionsInYear = 0;
        }
    }
    const endingAccumulationBalance = balance;
    const projectedRetirementCorpus = endingAccumulationBalance + (inputs.expectedLumpSumAtRetirement || 0);
    return {
        monthlyRate,
        months,
        years: years_,
        endingAccumulationBalance,
        projectedRetirementCorpus,
        totalContributions,
        yearsUntilRetirement: years,
    };
}
exports.projectAccumulation = projectAccumulation;
/**
 * Future value at retirement of a ₹1 starting monthly SIP with the
 * given annual step-up and monthly rate. This is the "unit" factor
 * used by the gap-solver to invert
 *   requiredSip = requiredFVofSip / unitFactor.
 *
 * Extracted so we don't re-run the whole month loop when solving.
 */
function unitStepUpSipFutureValueFactor(inputs) {
    const years = accumulationYearsFrom(inputs);
    const totalMonths = years * 12;
    const monthlyRate = (0, rates_1.effectiveAnnualToMonthly)(inputs.accumulationReturn);
    const step = inputs.annualSipStepUp || 0;
    if (totalMonths === 0)
        return 0;
    let balance = 0;
    for (let m = 1; m <= totalMonths; m++) {
        balance = balance * (1 + monthlyRate);
        const stepUps = Math.floor((m - 1) / 12);
        balance += Math.pow(1 + step, stepUps);
    }
    return balance;
}
exports.unitStepUpSipFutureValueFactor = unitStepUpSipFutureValueFactor;
